import { useState, useRef, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import {useAuth} from '../Auth/AuthContext'; 
import styles from './Header.module.css';
import LOGOS from '../../assets/images/LOGOS.png';

const Header = () => {
    const [menuVisible, setMenuVisible] = useState(false);
    const [profileDropdownVisible, setProfileDropdownVisible] = useState(false);
    const [tokenCheckInterval, setTokenCheckInterval] = useState(null);
    const navigate = useNavigate();
    const menuRef = useRef(null);
    const profileDropDownRef = useRef(null); 

    const { 
        isAuthenticated, 
        userData, 
        logout, 
        loading, 
        isAdmin,
        isTutor,
        getToken,
        handleAuthError
    } = useAuth(); 

    // Función para verificar el estado del token
    const checkTokenStatus = useCallback(() => {
        if (!isAuthenticated) return;

        const token = getToken();
        if (!token) {
            handleTokenExpired();
            return;
        }

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            const timeUntilExpiry = payload.exp - currentTime;

            // Si el token ya expiró
            if (timeUntilExpiry <= 0) {
                handleTokenExpired();
                return;
            }

        } catch (error) {
            console.error('Error verificando token:', error);
            handleTokenExpired();
        }
    }, [isAuthenticated, getToken]);

    // Manejar token expirado
    const handleTokenExpired = () => {
        handleAuthError(
            { 
                code: 'TOKEN_EXPIRED', 
                error: 'Tu sesión ha expirado' 
            }, 
            401
        );
        clearInterval(tokenCheckInterval);
        setTokenCheckInterval(null);
    };

    // Inicializar verificación periódica del token
    useEffect(() => {
        if (isAuthenticated && !loading) {
            // Verificar inmediatamente
            checkTokenStatus();

            // Configurar verificación cada 5 minutos
            const interval = setInterval(checkTokenStatus, 30000); // 5 minutos
            setTokenCheckInterval(interval);

            return () => {
                if (interval) {
                    clearInterval(interval);
                }
            };
        } else if (tokenCheckInterval) {
            // Si el usuario no está autenticado, limpiar el interval
            clearInterval(tokenCheckInterval);
            setTokenCheckInterval(null);
        }
    }, [isAuthenticated, loading, checkTokenStatus]);

    // Limpiar interval al desmontar el componente
    useEffect(() => {
        return () => {
            if (tokenCheckInterval) {
                clearInterval(tokenCheckInterval);
            }
        };
    }, [tokenCheckInterval]);

    const handleLogout = () => {
        if (tokenCheckInterval) {
            clearInterval(tokenCheckInterval);
            setTokenCheckInterval(null);
        }
        logout(); 
        setProfileDropdownVisible(false);
        navigate('/');
    };

    const getUserImage = () => {
        if (userData && userData.profile_image) {
            return userData.profile_image;
        }
        
        if (userData && userData.image) {
            return `${process.env.REACT_APP_API_URL}/perfil/profile-images/${userData.image}`;
        }
        
        return null; 
    };

    const navigateTo = (route) => {
        navigate(route);
        closeMenu();
        setProfileDropdownVisible(false);
    };

    const openMenu = () => {
        setMenuVisible(true);
    };

    const closeMenu = () => {
        setMenuVisible(false);
    };

    const toggleProfileDropdown = () => { 
        setProfileDropdownVisible(!profileDropdownVisible);
    }

    // Función para verificar si el usuario es administrador o tutor
    const isAdminOrTutor = () => {
        return isAdmin() || isTutor();
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (menuRef.current && !menuRef.current.contains(event.target) && menuVisible) {
                closeMenu();
            }
            if(profileDropDownRef.current && !profileDropDownRef.current.contains(event.target) && profileDropdownVisible) {
                setProfileDropdownVisible(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [menuVisible, profileDropdownVisible]);

    // Elementos del menú base
    const allMenuItems = [
        { text: "Conoce los temas", route: "/temas" },
        { text: "Acerca del programa", route: "/about-program" },
        { text: "Soy padre de familia", route: "/tutor" },
        { text: "Mapa de universidades", route: "/mapaUniversidades"},
    ];

    // Filtrar elementos del menú según el tipo de usuario
    const getFilteredMenuItems = () => {
        if (isAdminOrTutor()) {
            // Excluir "Conoce los temas" para administradores y tutores
            return allMenuItems.filter(item => item.text !== "Conoce los temas");
        }
        return allMenuItems;
    };

    const menuItems = getFilteredMenuItems();

    const getInitial = () => {
        if (userData && userData.nombre) {
            return userData.nombre.charAt(0).toUpperCase();
        }
        return 'U'; 
    };

    if (loading) { 
        return (
            <header className={styles.header}>
                <div className={styles.container}>
                    <img src={LOGOS} alt="Logo" className={styles.logo} />
                    <div>Cargando...</div>
                </div>
            </header>
        ); 
    }

    return (
        <div className={styles.header}>
            <img
                src={LOGOS}
                className={styles.logo}
                alt="Logo"
                onClick={() => navigateTo('/')}
                style={{ cursor: 'pointer' }}
            />

            <div className={styles.desktopMenu}>
                {menuItems.map((item, index) => (
                    <div
                        key={index}
                        className={styles.desktopMenuItem}
                        onClick={() => navigateTo(item.route)}
                    >
                        <span>{item.text}</span>
                    </div>
                ))}

                {isAuthenticated ? (
                    <div className={styles.profileContainer} ref={profileDropDownRef}>
                        <div 
                            className={`${styles.desktopMenuItem} ${styles.profileIcon}`}
                            onClick={toggleProfileDropdown}
                            title={userData?.nombre || 'Perfil'}
                        >
                            {getUserImage() ? (
                                <div className={styles.userAvatar}>
                                    <img src={getUserImage()} alt="Perfil" className={styles.userAvatarImage} />
                                </div>
                            ) : (
                                <div className={styles.userAvatar}>
                                    {getInitial()}
                                </div>
                            )}
                        </div>
                        
                        {profileDropdownVisible && (
                            <div className={styles.profileDropdown}>
                                <div 
                                    className={styles.dropdownItem}
                                    onClick={() => navigateTo('/profile')}
                                >
                                    Mi perfil
                                </div>

                                {/* Solo mostrar "Carrera universitaria" si NO es administrador ni tutor */}
                                {!isAdminOrTutor() && (
                                    <div 
                                        className={styles.dropdownItem}
                                        onClick={() => navigateTo('/carrera-universitaria')}
                                    >
                                        Carrera universitaria
                                    </div>
                                )}

                                {isAdmin() && (
                                    <div
                                        className={styles.dropdownItem}
                                        onClick={() => navigateTo('/ControlPanel')}
                                    >
                                        Panel de administrador 
                                    </div>
                                )}

                                <div 
                                    className={`${styles.dropdownItem} ${styles.logoutDropdown}`}
                                    onClick={handleLogout}
                                >
                                    Cerrar sesión
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        className={`${styles.desktopMenuItem} ${styles.loginButtonDesktop}`}
                        onClick={() => navigateTo('/signin')}
                    >
                        <span>Iniciar sesión</span>
                    </div>
                )}
            
            </div>

            <button className={styles.menuToggle} onClick={openMenu}>☰</button>

            {menuVisible && (
                <div className={`${styles.modalOverlay} ${menuVisible ? styles.active : ''}`} onClick={closeMenu}>
                    <div
                        className={`${styles.modalContent} ${menuVisible ? styles.active : ''}`}
                        ref={menuRef}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {menuItems.map((item, index) => (
                            <div
                                key={index}
                                className={styles.menuItem}
                                onClick={() => navigateTo(item.route)}
                            >
                                <span className={styles.menuItemText}>{item.text}</span>
                            </div>
                        ))}

                        {isAuthenticated ? (
                            <>
                                <div
                                    className={styles.menuItem}
                                    onClick={() => navigateTo('/profile')}
                                >
                                    <span className={styles.menuItemText}>Mi Perfil</span>
                                </div>

                                {isAdmin() && (
                                    <div
                                        className={styles.menuItem}
                                        onClick={() => navigateTo('/ControlPanel')}
                                    >
                                        <span className={styles.menuItemText}>Panel de Administrador</span>
                                    </div>
                                )}

                                <div
                                    className={`${styles.menuItem} ${styles.logoutItem}`}
                                    onClick={handleLogout}
                                >
                                    <span className={styles.menuItemText}>Cerrar sesión</span>
                                </div>
                            </>
                        ) : (
                            <div
                                className={`${styles.loginItem} ${styles.loginButton}`}
                                onClick={() => navigateTo('/signin')}
                            >
                                <span className={styles.loginButtonText}>Iniciar sesión</span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Header;